/**
 * Simple Variant Updater
 * Updates both SKU and availability messages when variants change
 * Uses optimized section rendering for better performance
 */

class SimpleVariantUpdater {
  constructor() {
    this.init();
  }

  init() {
    console.log('🚀 SimpleVariantUpdater: Initializing...');
    
    // Find the elements to update
    this.availabilityWrapper = document.querySelector('[data-availability-message]');
    this.skuWrapper = document.querySelector('[data-variant-sku]'); // Main product form SKU
    this.accordionSkuWrapper = document.querySelector('[data-accordion-sku]'); // Accordion SKU
    
    if (!this.availabilityWrapper && !this.skuWrapper && !this.accordionSkuWrapper) {
      console.warn('❌ No availability message or SKU wrapper found');
      return;
    }

    this.productHandle = this.availabilityWrapper?.dataset.productHandle;
    
    console.log('📍 SimpleVariantUpdater: Found elements:', {
      availabilityWrapper: !!this.availabilityWrapper,
      skuWrapper: !!this.skuWrapper,
      accordionSkuWrapper: !!this.accordionSkuWrapper,
      productHandle: this.productHandle,
      currentPath: window.location.pathname
    });

    // Listen for variant changes
    this.setupEventListener();

    console.log('✅ SimpleVariantUpdater: Initialized successfully');
  }

  setupEventListener() {
    // Listen for theme:variant:change events
    document.addEventListener('theme:variant:change', (event) => {
      const variant = event.detail.variant;
      if (variant) {
        console.log('🎯 Variant change detected:', variant.id, variant.sku);
        this.updateElements(variant);
      }
    });

    console.log('🎧 Event listener attached for theme:variant:change');
  }

  async updateElements(variant) {
    console.log('🔄 Updating elements for variant:', variant.id);

    // Update SKU immediately (we have the data)
    this.updateSKU(variant);

    // Update availability message (requires fetch)
    await this.updateAvailabilityMessage(variant.id);
  }

  updateSKU(variant) {
    const skuText = variant.sku || 'No SKU';
    const skuLabel = theme?.strings?.sku || 'SKU';
    
    // Update main product form SKU if it exists
    if (this.skuWrapper) {
      this.skuWrapper.innerHTML = `${skuLabel}: ${skuText}`;
      console.log('✅ Main SKU updated:', skuText);
    }
    
    // Update accordion SKU if it exists
    if (this.accordionSkuWrapper) {
      this.accordionSkuWrapper.innerHTML = `SKU: ${skuText}`;
      console.log('✅ Accordion SKU updated:', skuText);
    }
    
    if (!this.skuWrapper && !this.accordionSkuWrapper) {
      console.log('⚠️ No SKU wrapper found');
    }
  }

  async updateAvailabilityMessage(variantId) {
    if (!this.availabilityWrapper) {
      console.log('⚠️ No availability wrapper found');
      return;
    }

    console.log('🔄 Updating availability message for variant:', variantId);

    try {
      // Show loading state
      this.showLoadingState();
      
      // Try section rendering first (faster)
      let success = await this.updateUsingSection(variantId);
      
      // Fallback to full page if section fails
      if (!success) {
        console.log('🔄 Section failed, trying fallback approach...');
        await this.updateUsingFullPage(variantId);
      }
      
    } catch (error) {
      console.error('❌ Failed to update availability message:', error);
    } finally {
      this.hideLoadingState();
    }
  }

  async updateUsingSection(variantId) {
    try {
      const url = `${window.location.pathname}?section_id=api-availability-message&variant=${variantId}`;
      console.log('📡 Trying section rendering:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        console.log('❌ Section rendering failed:', response.status);
        return false;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newContent = doc.querySelector('.availability-message-content');
      
      if (newContent) {
        this.availabilityWrapper.innerHTML = newContent.innerHTML;
        console.log('✅ Availability updated via section rendering');
        return true;
      }
      
      return false;
    } catch (error) {
      console.log('❌ Section rendering error:', error.message);
      return false;
    }
  }

  async updateUsingFullPage(variantId) {
    const url = `${window.location.pathname}?variant=${variantId}`;
    console.log('📡 Fetching full page:', url);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const newAvailabilityElement = doc.querySelector('[data-availability-message]');
    
    if (newAvailabilityElement) {
      this.availabilityWrapper.innerHTML = newAvailabilityElement.innerHTML;
      console.log('✅ Availability updated via full page');
    } else {
      console.warn('❌ No availability message found in response');
    }
  }

  showLoadingState() {
    if (this.availabilityWrapper) {
      this.availabilityWrapper.style.opacity = '0.6';
      this.availabilityWrapper.style.transition = 'opacity 0.2s';
      console.log('⏳ Showing loading state');
    }
  }

  hideLoadingState() {
    if (this.availabilityWrapper) {
      this.availabilityWrapper.style.opacity = '';
      this.availabilityWrapper.style.transition = '';
      console.log('✨ Loading state cleared');
    }
  }
}

// Initialize when DOM is ready
function initSimpleVariantUpdater() {
  console.log('🔧 SimpleVariantUpdater: DOM ready, initializing...');
  new SimpleVariantUpdater();
}

// Multiple initialization strategies
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSimpleVariantUpdater);
} else {
  // DOM is already loaded
  initSimpleVariantUpdater();
}

// Also try on window load as fallback
window.addEventListener('load', () => {
  setTimeout(() => {
    if (!window.simpleVariantUpdaterInitialized) {
      console.log('🔄 SimpleVariantUpdater: Fallback initialization');
      initSimpleVariantUpdater();
    }
  }, 100);
});

// Mark as initialized
window.simpleVariantUpdaterInitialized = true; 